import React from 'react';
import styled from 'styled-components';

import { ResponseRecordRow, SupportActionIcon } from '../types';
import { ResponseIcon } from './ResponseActionButton';
import { responseTone, responseType, rsColor, rsFor, rsv } from './responseScale';

/*
  The chrome the page response panels share (gap reports g2 S3-S9, g3 S6-S7): intro line, panel,
  head, body and key-value rows. A type that draws something else styles its own.
*/

/**
 * The reply: the intro line, then the panel 13.5 below it (6265:4221 → 6265:4222). Each type caps
 * its own width: 888 by default, 845 for multi-answer, 960 for the hand-off; 445 in the panel.
 */
export const ResponseReply = styled.div<{ $column?: 'replyColumn' | 'replyColumnNarrow' }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-width: ${props => rsFor(props).size[props.$column ?? 'replyColumn']}px;

  > * + * {
    margin-top: ${rsv.space('introToPanel')}px;
  }
`;

export const ResponseIntro = styled.p`
  margin: 0;
  color: ${rsColor.secondary};
  white-space: pre-line;
  ${responseType('intro')}
`;

export const ResponsePanel = styled.section`
  border: 1px solid ${rsColor.border};
  border-radius: ${rsv.radius('panel')}px;
  background: ${rsColor.panel};
  overflow: hidden;
`;

export const ResponsePanelHead = styled.header`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-height: ${rsv.size('panelHead')}px;
  padding: ${rsv.space('panelHead')};
  border-bottom: 1px solid ${rsColor.border};
  color: ${rsColor.text};

  > * + * {
    margin-left: ${rsv.space('headIconGap')}px;
  }

  .anticon {
    font-size: ${rsv.size('headIcon')}px;
  }
`;

export const ResponsePanelTitle = styled.h4`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  color: ${rsColor.text};
  ${responseType('panelTitle')}
`;

export const ResponsePanelBody = styled.div`
  padding: ${rsv.space('panelBody')};

  &:first-child {
    padding-top: ${rsv.space('headlessBodyTop')}px;
  }
`;

/** A titled panel head: the payload's glyph, then its title. */
export const ResponseHead: React.FC<{
  title: string;
  icon?: SupportActionIcon | null;
  children?: React.ReactNode;
}> = ({ title, icon, children }) => (
  <ResponsePanelHead>
    <ResponseIcon icon={icon} />
    <ResponsePanelTitle>{title}</ResponsePanelTitle>
    {children}
  </ResponsePanelHead>
);

const InlineRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  min-height: ${rsv.size('row')}px;
  padding: ${rsv.space('row')};
  padding-right: ${rsv.space('rowInset')}px;
  padding-left: ${rsv.space('rowInset')}px;
  border-bottom: 1px solid ${rsColor.border};
  ${responseType('body')}

  > dt {
    flex: 0 0 auto;
    color: ${rsv.color('label')};
  }

  > dd {
    min-width: 0;
    margin: 0 0 0 ${rsv.space('rowGap')}px;
    font-weight: 700;
    text-align: right;
  }
`;

const StackedRow = styled.div`
  padding: ${rsv.space('row')};
  border-bottom: 1px solid ${rsColor.border};
  ${responseType('body')}

  > dt {
    color: ${rsv.color('label')};
  }

  > dd {
    margin: ${rsv.space('stackedGap')}px 0 0;
    color: ${rsColor.secondary};
    white-space: pre-line;
  }
`;

// 6604:1493: 37 x 23 under 5 / 11 padding.
export const ResponsePill = styled.span<{ $ink: string; $fill: string }>`
  display: inline-flex;
  align-items: center;
  padding: ${rsv.space('pill')};
  border-radius: ${rsv.radius('pill')}px;
  background: ${({ $fill }) => $fill};
  color: ${({ $ink }) => $ink};
  white-space: nowrap;
  ${responseType('pill')}
`;

/** One labelled value, 47 tall inline (6265:4256) or label-over-body stacked (6265:4288). */
export const ResponseRow: React.FC<{ row: ResponseRecordRow }> = ({ row }) => {
  const color = row.tone ? responseTone(row.tone).ink : rsColor.text;
  if (row.variant === 'pill') {
    const tone = responseTone(row.tone);
    return (
      <InlineRow data-testid="orbit-response-row">
        <dt>{row.label}</dt>
        <dd>
          <ResponsePill $ink={tone.ink} $fill={tone.fill}>
            {row.value}
          </ResponsePill>
        </dd>
      </InlineRow>
    );
  }
  if (row.layout === 'stacked') {
    return (
      <StackedRow data-testid="orbit-response-row">
        <dt>{row.label}</dt>
        <dd style={row.tone ? { color } : undefined}>{row.value}</dd>
      </StackedRow>
    );
  }
  return (
    <InlineRow data-testid="orbit-response-row">
      <dt>{row.label}</dt>
      <dd style={{ color }}>{row.value}</dd>
    </InlineRow>
  );
};

export const ResponseRows = styled.dl`
  margin: 0;
`;
